/**
 * CONTRACT.md §2.12 — file rows, the locked block and the masked value.
 *
 * `FileIcon` is a **free-standing** type badge. §2.12: "It is at home inside a
 * `.frow`, but it is also the correct element for a bare ZIP/FIG/KEY/PDF marker
 * inside a `.locked` block or a `.row`, where nesting a whole `.frow` would
 * double the `--sp-4` horizontal padding." §5 refused `.ftype` for that reason,
 * so this is one component used in two places rather than two components.
 *
 * `Preview` (session 48) is a mockup's page, framed at a device's width. It is the one
 * element here whose contents come from somewhere else: the frame is sandboxed twice —
 * by its own `sandbox` attribute and by the CSP the page is served under
 * (`modules/mockups/routes.ts`) — so neither layer is the only one.
 *
 * `Dock` (session 49) is the portal in one corner of a mockup shown full-screen (`Preview`
 * with `fill`): a bar of a few buttons, and a body that opens under it. It is the whole of
 * the portal on a page that is otherwise the studio's, so it stays small until asked.
 *
 * Session 55 (`.docs/55-mockup-comments.md`) makes that viewer the one place a mockup is
 * reviewed. The dock can be a **panel** — open on a wide screen it is a column down the right
 * edge, and the page (`Preview` with `beside`) is drawn beside it rather than under it. A
 * thread opens in a `PinCard` next to its pin, placed by where the frame says the pin is;
 * `PinNo` is a pin's number as the list says it, the frame's marker in miniature; and a
 * `ThreadRow` is one thread in the list.
 *
 * `Mask` is `properties/••••••••42` — a masked credential tail. It is a ref, so
 * mono is correct; the characters are already dots by the time they arrive here,
 * because `apps/api` is where a secret is masked and revealing one is an audited
 * route (`clients.service.revealCredential`).
 */

import type { CSSProperties, ReactNode, Ref } from 'react'

import { cx, type Common } from './common.js'

export type FileRowProps = Common & {
  /** The type badge — `pdf`, `zip`, `ga4`. Rendered uppercase by app.css. */
  readonly icon: string
  readonly title: ReactNode
  /** The `.xs muted` line under the title — "Added 3 Aug 2026". */
  readonly sub?: ReactNode | undefined
  /** The right-hand group: a `DateText`, a `More` link, a `Button`. */
  readonly meta: ReactNode
}

export function FileRow({ icon, title, sub, meta, ...common }: FileRowProps) {
  return (
    <div className="frow" {...common}>
      <FileIcon>{icon}</FileIcon>
      <div>
        <span className="b">{title}</span>
        {sub === undefined ? null : <div className="xs muted">{sub}</div>}
      </div>
      <div className="frow__meta">{meta}</div>
    </div>
  )
}

export type FileIconProps = Common & { readonly children: ReactNode }

export function FileIcon({ children, ...common }: FileIconProps) {
  return (
    <span className="frow__ic" {...common}>
      {children}
    </span>
  )
}

export type LockedProps = Common & { readonly children: ReactNode }

/** The dashed block for deliverables held until an invoice is paid. */
export function Locked({ children, ...common }: LockedProps) {
  return (
    <div className="locked" {...common}>
      {children}
    </div>
  )
}

export type MaskProps = Common & { readonly children: ReactNode }

export function Mask({ children, ...common }: MaskProps) {
  return (
    <span className="mask" {...common}>
      {children}
    </span>
  )
}

/** The widths a mockup can be looked at: the window's, a tablet's and a phone's. */
export const PREVIEW_WIDTHS = ['desktop', 'tablet', 'phone'] as const

export type PreviewWidth = (typeof PREVIEW_WIDTHS)[number]

export type PreviewProps = Common & {
  /** The page — a mockup version's public URL. */
  readonly src: string
  /** What a screen reader announces for the frame — "Homepage redesign, version 2". */
  readonly title: string
  readonly width: PreviewWidth
  /**
   * The whole window, edge to edge, rather than a framed panel in a region (session 49) — the
   * public viewer, where the page is the studio's and the portal is one corner.
   */
  readonly fill?: boolean | undefined
  /** The frame itself — what the pin bridge (`pins/usePinBridge.ts`) talks to. */
  readonly frameRef?: Ref<HTMLIFrameElement> | undefined
  /**
   * With `fill`: room on the right for an open `Dock` with `panel`, on a screen wide enough
   * to have both side by side (session 55).
   */
  readonly beside?: boolean | undefined
  /** The area the page is shown in — what a `PinCard` is kept inside. */
  readonly areaRef?: Ref<HTMLDivElement> | undefined
}

/**
 * A mockup's page in a frame at a device's width.
 *
 * `sandbox` without `allow-same-origin`: the page's scripts run, in an opaque origin, which
 * is the server's own rule said again on this side. `no-referrer`, because the portal's
 * address is nothing a page the studio built needs to know.
 */
export function Preview({
  src,
  title,
  width,
  fill,
  frameRef,
  beside,
  areaRef,
  ...common
}: PreviewProps) {
  return (
    <div
      ref={areaRef}
      className={cx(
        'preview',
        `preview--${width}`,
        fill === true && 'preview--fill',
        fill === true && beside === true && 'preview--beside',
      )}
      {...common}
    >
      <iframe
        ref={frameRef}
        className="preview__frame"
        src={src}
        title={title}
        sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads"
        referrerPolicy="no-referrer"
      />
    </div>
  )
}

export type DockProps = Common & {
  /** Open, the body shows under the bar; closed, the bar is all there is. */
  readonly open: boolean
  /**
   * Open, but out of the way for a moment — the bar alone, with the body kept (hidden, not
   * unmounted), so a comment half written survives somebody tapping the page to pin it.
   */
  readonly tucked?: boolean | undefined
  /** The buttons that are always there. */
  readonly bar: ReactNode
  /** What opening shows. */
  readonly children?: ReactNode
  /** What a screen reader calls the corner — "Comments on this mockup". */
  readonly label: string
  /**
   * Open on a wide screen, a column down the right edge rather than a box in the corner
   * (session 55) — the list of a review, beside a `Preview` with `beside`.
   */
  readonly panel?: boolean | undefined
}

/** The portal, docked in one corner of a page that is somebody else's (session 49). */
export function Dock({ open, tucked, bar, children, label, panel, ...common }: DockProps) {
  const shown = open && tucked !== true
  return (
    <aside
      className={cx('dock', shown && 'dock--open', panel === true && 'dock--panel')}
      aria-label={label}
      {...common}
    >
      <div className="dock__bar">{bar}</div>
      {open ? (
        <div className="dock__body" hidden={!shown}>
          {children}
        </div>
      ) : null}
    </aside>
  )
}

export type PinCardProps = Common & {
  /**
   * Where its top-left corner goes, in the window — worked out from where the frame says the
   * pin is (`pages/mockups/review.ts` `cardPlace`). On a phone the card is a sheet along the
   * bottom edge instead, and this is not read.
   */
  readonly at: { readonly left: number; readonly top: number }
  /** What a screen reader calls it — "Comment 3", "New comment". */
  readonly label: string
  readonly children: ReactNode
  /** The card itself — measured, so it is kept inside the page's area. */
  readonly cardRef?: Ref<HTMLElement> | undefined
}

/**
 * A thread, next to its pin, over a mockup's page (session 55). Not a dialog: it traps nothing
 * and dims nothing, because the page it is about is still the point — the dock's rule.
 *
 * **Its place is CONTRACT.md §4.1's fourth sanctioned inline value**: `--at-x`/`--at-y`,
 * because where a pin is on somebody else's page is a fact of that page, not a class.
 */
export function PinCard({ at, label, children, cardRef, ...common }: PinCardProps) {
  const place = {
    '--at-x': `${String(at.left)}px`,
    '--at-y': `${String(at.top)}px`,
  } as CSSProperties
  return (
    <section ref={cardRef} className="pincard" aria-label={label} style={place} {...common}>
      {children}
    </section>
  )
}

export type PinNoProps = Common & {
  readonly n: number
  /** A resolved thread's — faded, as its pin is on the page. */
  readonly done?: boolean | undefined
}

/** A pin's number, as the list and the thread say it — the frame's marker, small (session 55). */
export function PinNo({ n, done, ...common }: PinNoProps) {
  return (
    <span className={cx('pinno', done === true && 'pinno--done')} {...common}>
      {n}
    </span>
  )
}

export type ThreadRowProps = Common & {
  /** The pin's number — or nothing, for a thread about the whole version. */
  readonly pin: ReactNode
  readonly children: ReactNode
  /** The thread open now. */
  readonly on?: boolean | undefined
  /** A resolved thread, muted. */
  readonly done?: boolean | undefined
  readonly onClick: () => void
}

/** One thread in a review's list: a button, because pressing it opens the thread (session 55). */
export function ThreadRow({ pin, children, on, done, onClick, ...common }: ThreadRowProps) {
  return (
    <button
      type="button"
      className={cx(
        'thread-row',
        on === true && 'thread-row--on',
        done === true && 'thread-row--done',
      )}
      aria-pressed={on === true}
      onClick={onClick}
      {...common}
    >
      <span className="thread-row__pin">{pin}</span>
      <span className="thread-row__text">{children}</span>
    </button>
  )
}
